import { FUSEAU_PAR_DEFAUT, jourDansFuseau, type CustomFieldValues } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';

/**
 * LE JEU DE DEMONSTRATION de l'app Patients : 8 maitres et 12 animaux d'une
 * clinique veterinaire belge. Des noms et des animaux inventes, sans rien de
 * reel.
 *
 * CE QUE CHAQUE LIGNE SERT :
 *   - Rex, un berger belge de onze ans, cardiaque : le patient au long
 *     historique (Consultations), a la courbe de poids qui descend ;
 *   - une maitresse a deux animaux (Claire), un animal a deux maitres (Mimi, et
 *     Gaston) ;
 *   - un animal decede (Biscotte), un chaton de onze semaines (Pepito) ;
 *   - quatre especes de plan de vaccination (chien, chat, lapin, cheval) et un
 *     cochon d'Inde, qui n'a pas de plan ;
 *   - des seniors (Rex, Luna, Gaston, Biscotte), des alertes de tous les tons.
 *
 * Les soins et les consultations de ces animaux sont ceux de l'app
 * Consultations, qui les retrouve ici par leur `demoKey` (`pa-a*`) : la demo du
 * Catalogue d'abord, puis celle-ci, puis celle des Consultations.
 *
 * Memes regles que `../taches/demo-dataset.ts` : tout passe par `@taki/sdk`,
 * IDEMPOTENT par `customFields.demoKey` (`pa-*`), aucune suppression, AUCUNE
 * DATE FIGEE (la naissance se calcule depuis aujourd'hui, dans le fuseau de
 * l'org). Une date PURE est une chaine `YYYY-MM-DD`, jamais un Timestamp. Les
 * valeurs sont les valeurs STOCKEES des catalogues.
 */

const TYPE_ANIMAL = 'animal';
const TYPE_PERSONNE = 'person';

/** Ce que l'import a fait, rendu au hub Parametres. */
export interface DemoImportResult {
  created: number;
  skipped: number;
  message: string;
}

export interface DemoMaitre {
  demoKey: string;
  civility: 'mr' | 'mrs';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobile?: string;
  street: string;
  zip: string;
  city: string;
  lang: 'fr' | 'de' | 'nl';
  notes?: string;
}

export interface DemoAnimal {
  demoKey: string;
  name: string;
  species: string;
  breed?: string;
  coat?: string;
  sex: 'male' | 'femelle';
  /** Age en jours a aujourd'hui : la naissance se calcule, elle ne se fige pas. */
  ageJours: number;
  weightKg: number;
  neutered?: boolean;
  /** `vivant`, `decede`. */
  status: string;
  /** Le numero de puce. */
  reference?: string;
  /** La `demoKey` du maitre. */
  maitre: string;
  /** La `demoKey` du second maitre. */
  secondMaitre?: string;
  alerts?: ReadonlyArray<string>;
  internalNotes?: string;
}

export const DEMO_MAITRES: ReadonlyArray<DemoMaitre> = [
  { demoKey: 'pa-p1', civility: 'mrs', firstName: 'Claire', lastName: 'Lambert', email: 'claire.lambert@exemple.be', phone: '+32 81 22 14 08', mobile: '+32 475 11 22 33', street: 'Rue de la Station 14', zip: '5000', city: 'Namur', lang: 'fr' },
  { demoKey: 'pa-p2', civility: 'mr', firstName: 'Thomas', lastName: 'Peeters', email: 'thomas.peeters@exemple.be', phone: '+32 478 44 55 66', street: 'Chaussée de Marche 212', zip: '5100', city: 'Jambes', lang: 'fr' },
  { demoKey: 'pa-p3', civility: 'mrs', firstName: 'Nadia', lastName: 'Benali', email: 'nadia.benali@exemple.be', phone: '+32 470 77 88 99', street: 'Avenue Reine Astrid 7', zip: '5000', city: 'Namur', lang: 'fr' },
  { demoKey: 'pa-p4', civility: 'mr', firstName: 'Luc', lastName: 'Janssens', email: 'luc.janssens@exemple.be', phone: '+32 496 12 34 56', street: 'Ferme du Grand Pré, route de Wépion 3', zip: '5100', city: 'Wépion', lang: 'fr', notes: 'Éleveur. Préfère être appelé après 18 h.' },
  { demoKey: 'pa-p5', civility: 'mrs', firstName: 'Sophie', lastName: 'Dubois', email: 'sophie.dubois@exemple.be', phone: '+32 486 65 43 21', street: 'Rue des Tilleuls 31', zip: '5030', city: 'Gembloux', lang: 'fr' },
  { demoKey: 'pa-p6', civility: 'mr', firstName: 'Marc', lastName: 'Vandenberghe', email: 'marc.vandenberghe@exemple.be', phone: '+32 472 90 13 57', street: 'Rue du Moulin 9', zip: '5004', city: 'Bouge', lang: 'nl' },
  { demoKey: 'pa-p7', civility: 'mrs', firstName: 'Isabelle', lastName: 'Renard', email: 'isabelle.renard@exemple.be', phone: '+32 81 74 02 66', street: 'Place Saint-Aubain 2', zip: '5000', city: 'Namur', lang: 'fr' },
  { demoKey: 'pa-p8', civility: 'mr', firstName: 'Pierre', lastName: 'Gillet', email: 'pierre.gillet@exemple.be', phone: '+32 495 28 71 40', street: 'Chemin des Écuries 18', zip: '5330', city: 'Assesse', lang: 'fr' },
];

const AN = 365;

export const DEMO_ANIMAUX: ReadonlyArray<DemoAnimal> = [
  { demoKey: 'pa-a1', name: 'Rex', species: 'Chien', breed: 'Berger belge', coat: 'Fauve charbonné', sex: 'male', ageJours: AN * 11 + 40, weightKg: 28.6, neutered: true, status: 'vivant', reference: '981100012345678', maitre: 'pa-p1',
    alerts: ['Cardiaque', 'Mord'], internalNotes: 'Insuffisance mitrale suivie depuis un an. Stressé chez le vétérinaire : muselière pour les vaccins.' },
  { demoKey: 'pa-a2', name: 'Mimi', species: 'Chat', breed: 'Européen', coat: 'Tigrée grise', sex: 'femelle', ageJours: AN * 5 + 100, weightKg: 4.2, neutered: true, status: 'vivant', reference: '981100012345679', maitre: 'pa-p1', secondMaitre: 'pa-p5' },
  { demoKey: 'pa-a3', name: 'Billie', species: 'Chien', breed: 'Labrador', coat: 'Sable', sex: 'femelle', ageJours: AN * 2, weightKg: 26.1, neutered: false, status: 'vivant', reference: '981100012345680', maitre: 'pa-p2' },
  { demoKey: 'pa-a4', name: 'Pixel', species: 'Chat', breed: 'Maine coon', coat: 'Brun tabby', sex: 'male', ageJours: AN + 200, weightKg: 5.8, neutered: false, status: 'vivant', maitre: 'pa-p3', alerts: ['Griffe', 'Craintif'] },
  { demoKey: 'pa-a5', name: 'Caramel', species: 'Lapin', breed: 'Bélier nain', coat: 'Fauve', sex: 'femelle', ageJours: AN * 4, weightKg: 2, neutered: true, status: 'vivant', maitre: 'pa-p3' },
  { demoKey: 'pa-a6', name: 'Gaston', species: 'Cheval', breed: 'Trait belge', coat: 'Alezan', sex: 'male', ageJours: AN * 12, weightKg: 779, neutered: true, status: 'vivant', maitre: 'pa-p4', secondMaitre: 'pa-p8', alerts: ['Agressif'], internalNotes: 'Muselière pour les soins. Le propriétaire le tient.' },
  { demoKey: 'pa-a7', name: 'Noisette', species: 'Rongeur', breed: 'Cochon d’Inde', coat: 'Tricolore', sex: 'femelle', ageJours: AN * 2 + 60, weightKg: 0.98, status: 'vivant', maitre: 'pa-p5' },
  { demoKey: 'pa-a8', name: 'Biscotte', species: 'Chien', breed: 'Cavalier King Charles', coat: 'Blenheim', sex: 'femelle', ageJours: AN * 14, weightKg: 8, neutered: true, status: 'decede', reference: '981100012345681', maitre: 'pa-p4' },
  { demoKey: 'pa-a9', name: 'Jazz', species: 'Chien', breed: 'Border collie', coat: 'Noir et blanc', sex: 'male', ageJours: AN * 5, weightKg: 18.4, neutered: true, status: 'vivant', reference: '981100012345682', maitre: 'pa-p6', alerts: ['Craintif', 'Allergie'], internalNotes: 'Allergie cutanée suivie. Peur des bruits : salle calme.' },
  { demoKey: 'pa-a10', name: 'Luna', species: 'Chat', breed: 'Siamois', coat: 'Seal point', sex: 'femelle', ageJours: AN * 14 + 50, weightKg: 4.3, neutered: true, status: 'vivant', reference: '981100012345683', maitre: 'pa-p7', alerts: ['Diabétique'], internalNotes: 'Insuline matin et soir. Perd du poids lentement.' },
  { demoKey: 'pa-a11', name: 'Orage', species: 'Cheval', breed: 'Selle français', coat: 'Bai', sex: 'male', ageJours: AN * 9, weightKg: 541, neutered: true, status: 'vivant', maitre: 'pa-p8' },
  { demoKey: 'pa-a12', name: 'Pépito', species: 'Chat', breed: 'Européen', coat: 'Roux', sex: 'male', ageJours: 77, weightKg: 1.6, neutered: false, status: 'vivant', maitre: 'pa-p2' },
];

/** Le nombre total de fiches que ce jeu pose, cite par le hub Parametres. */
export const DEMO_TOTAL = DEMO_MAITRES.length + DEMO_ANIMAUX.length;

/** Un jour, en date PURE : `YYYY-MM-DD`, jamais un instant. */
export function jourPurAvant(aujourdhui: string, jours: number): string {
  const [annee, mois, jour] = aujourdhui.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(annee, mois - 1, jour - jours)).toISOString().slice(0, 10);
}

/** Un sac sans clef vide : un champ sans valeur s'OMET. */
function sans<T extends Record<string, unknown>>(sac: T): CustomFieldValues {
  return Object.fromEntries(Object.entries(sac).filter(([, valeur]) => valeur !== undefined)) as CustomFieldValues;
}

/** Les `demoKey` deja en base pour une liste de fiches, avec leur id. */
function idsParClef(fiches: ReadonlyArray<{ id: string; customFields?: Record<string, unknown> }>): Map<string, string> {
  const table = new Map<string, string>();
  for (const fiche of fiches) {
    const clef = fiche.customFields?.['demoKey'];
    if (typeof clef === 'string') table.set(clef, fiche.id);
  }
  return table;
}

const fiches = (n: number): string => `${n} ${n > 1 ? 'fiches' : 'fiche'}`;

/**
 * ECRIRE LE JEU DANS L'ORGANISATION COURANTE, et ne creer que ce qui manque.
 * Les maitres d'abord : un animal pointe son maitre par `ownerContactId`.
 */
export async function importDemoDataset(sdk: TakiSdk, orgId: string): Promise<DemoImportResult> {
  let created = 0;
  let skipped = 0;
  const aujourdhui = jourDansFuseau(new Date(), sdk.timezone ?? FUSEAU_PAR_DEFAUT);

  /* ---- 1. Les maitres (contacts::person) ---- */
  const maitres = idsParClef(await sdk.contacts.list({ type: TYPE_PERSONNE, archived: 'all' }));
  for (const item of DEMO_MAITRES) {
    if (maitres.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const contact = await sdk.contacts.create({
      orgId,
      type: TYPE_PERSONNE,
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email,
      phone: item.phone,
      customFields: sans({
        civility: item.civility,
        mobile: item.mobile,
        relation: 'client',
        street: item.street,
        zip: item.zip,
        city: item.city,
        country: 'BE',
        lang: item.lang,
        notes: item.notes,
        demoKey: item.demoKey,
      }),
    });
    maitres.set(item.demoKey, contact.id);
    created += 1;
  }

  /* ---- 2. Les animaux (assets::animal) ---- */
  const animaux = idsParClef(await sdk.assets.list({ type: TYPE_ANIMAL, archived: 'all' }));
  for (const item of DEMO_ANIMAUX) {
    if (animaux.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const maitre = maitres.get(item.maitre);
    const second = item.secondMaitre === undefined ? undefined : maitres.get(item.secondMaitre);
    const animal = await sdk.assets.create({
      orgId,
      type: TYPE_ANIMAL,
      name: item.name,
      status: item.status,
      ...(item.reference === undefined ? {} : { reference: item.reference }),
      ...(maitre === undefined ? {} : { ownerContactId: maitre }),
      customFields: sans({
        species: item.species,
        breed: item.breed,
        coat: item.coat,
        sex: item.sex,
        birthDate: jourPurAvant(aujourdhui, item.ageJours),
        weightKg: item.weightKg,
        neutered: item.neutered,
        coOwnerId: second,
        alerts: item.alerts === undefined ? undefined : [...item.alerts],
        internalNotes: item.internalNotes,
        demoKey: item.demoKey,
      }),
    });
    animaux.set(item.demoKey, animal.id);
    created += 1;
  }

  const dejaLa = skipped === 0 ? "rien n'existait déjà" : `${skipped} déjà ${skipped > 1 ? 'présentes' : 'présente'}`;
  const message =
    created === 0
      ? `Le jeu de démonstration est déjà en place (${fiches(skipped)}).`
      : `${fiches(created)} ${created > 1 ? 'créées' : 'créée'}, ${dejaLa}.`;
  return { created, skipped, message };
}
